import { Link } from 'react-router-dom';
import { PHQ4Entry } from '../types';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, Cell,
} from 'recharts';
import { useLens } from '../lib/dashboard/lensContext';
import {
  mentalHealthSectionLabel,
  mentalHealthViewInsightsLabel,
  moodScreenLabel,
  primaryPatternLabel,
  screeningBadge,
  checkinPrompt,
  checkinCtaLabel,
  noDataPrompt,
  noDataCtaLabel,
  trendChartTitle,
  trendChartSubtitle,
  trendTooltipLabels,
  trendLegendLabels,
} from '../lib/dashboard/mentalHealthLensCopy';

interface Props {
  phq4Data: { q1: number; q2: number; q3: number; q4: number } | null;
  phq4ScreeningLevel: 'normal' | 'mild' | 'moderate' | 'severe';
  phenoPrimary: { name: string; score: number; color: string } | null;
  weeksBehind: number;
  hasEntries: boolean;
  phq4Entries: PHQ4Entry[];
}

/** Color a PHQ-4 score by severity band. */
function scoreColor(score: number): string {
  if (score >= 9) return '#E11D48'; // rose-600 — softer than pure red
  if (score >= 6) return '#EA580C'; // amber-orange
  if (score >= 3) return '#F59E0B'; // amber
  return '#16A34A';                 // green
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// NOTE: intentionally NOT wrapped in React.memo. The parent `Dashboard`
// passes a fresh props object on most renders, so the memo was almost
// never hitting its shallow-equality short-circuit anyway — it was
// adding comparison cost without saving renders. Keep this component
// as a plain function so the render path stays predictable and the
// context subscription to useLens propagates on every flip.
function DashboardMentalHealthInner({
  phq4Data, phq4ScreeningLevel, phenoPrimary, weeksBehind, hasEntries, phq4Entries,
}: Props) {
  const { lens } = useLens();

  // Build chart data from entries (newest first → reverse for chronological)
  const chartData = [...phq4Entries]
    .slice(0, 16)
    .reverse()
    .map(e => ({
      label: formatDate(e.created_at),
      score: e.score,
      depression: e.q1 + e.q2,
      anxiety: e.q3 + e.q4,
    }));

  const tooltipLabels = trendTooltipLabels(lens);
  const legendLabels  = trendLegendLabels(lens);

  return (
    <div
      data-testid="dashboard-mental-health"
      data-lens={lens}
      data-phase="6"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="section-label">{mentalHealthSectionLabel(lens)}</h2>
        <Link to="/phenotype" className="text-xs text-arete-blue hover:underline">
          {mentalHealthViewInsightsLabel(lens)}
        </Link>
      </div>
      {weeksBehind >= 1 && hasEntries && (
        <Link
          to="/checkin"
          className="flex items-center justify-between bg-arete-sageLight dark:bg-dark-card border border-arete-sage dark:border-dark-border rounded-xl px-4 py-2.5 mb-3 no-underline"
        >
          <span className="text-sm text-arete-forest dark:text-gray-200">
            {checkinPrompt(weeksBehind, lens)}
          </span>
          <span className="text-xs font-semibold whitespace-nowrap ml-4 text-arete-moss dark:text-arete-sage">
            {checkinCtaLabel(lens)}
          </span>
        </Link>
      )}
      {phq4Data ? (() => {
        const total = phq4Data.q1 + phq4Data.q2 + phq4Data.q3 + phq4Data.q4;
        const badge = screeningBadge(phq4ScreeningLevel, lens);
        return (
          <>
            <Link to="/phenotype" className="card block hover:shadow-card-hover transition-shadow no-underline mb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-arete-muted dark:text-gray-400">{moodScreenLabel(lens)}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-2xl font-bold text-arete-navy dark:text-gray-100">{total}</span>
                    <span className="text-sm text-arete-muted">/12</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${badge.bg}`}>
                      {badge.text}
                    </span>
                  </div>
                </div>
                {phenoPrimary && (
                  <div className="text-right">
                    <p className="text-xs text-arete-muted dark:text-gray-400">{primaryPatternLabel(lens)}</p>
                    <div className="flex items-center gap-1.5 mt-1 justify-end">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: phenoPrimary.color }} />
                      <span className="text-sm font-bold text-arete-navy dark:text-gray-100">{phenoPrimary.name}</span>
                    </div>
                  </div>
                )}
              </div>
            </Link>

            {/* Crisis resources — SAFETY-CRITICAL. Shown whenever PHQ-4 >= 9
                regardless of lens. Copy is intentionally identical in
                patient and clinician views — we never soften or alter it. */}
            {total >= 9 && (
              <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl px-4 py-3 mb-3" data-testid="crisis-banner">
                <p className="text-xs font-semibold text-rose-800 dark:text-rose-300 mb-1.5">
                  If you or someone you know is in crisis:
                </p>
                <div className="space-y-1">
                  <a href="tel:988" className="flex items-center gap-2 text-xs text-rose-700 dark:text-rose-300 hover:underline font-medium">
                    <span>📞</span> 988 Suicide & Crisis Lifeline — call or text 988
                  </a>
                  <a href="sms:741741&body=HELLO" className="flex items-center gap-2 text-xs text-rose-700 dark:text-rose-300 hover:underline font-medium">
                    <span>💬</span> Crisis Text Line — text HOME to 741741
                  </a>
                  <a href="https://988lifeline.org/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-rose-700 dark:text-rose-300 hover:underline font-medium">
                    <span>🌐</span> 988lifeline.org ↗
                  </a>
                </div>
                <p className="text-[10px] text-rose-500 dark:text-rose-400 mt-2">
                  This screening is not a diagnosis. Please reach out to a healthcare provider for support.
                </p>
              </div>
            )}

            {/* PHQ-4 Trend Chart */}
            {chartData.length >= 2 && (
              <div className="card">
                <h3 className="card-title mb-1">{trendChartTitle(lens)}</h3>
                <p className="text-[10px] text-arete-muted mb-3">
                  {trendChartSubtitle(lens)}
                </p>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -25 }}>
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: '#64748B' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      domain={[0, 12]}
                      ticks={[0, 3, 6, 9, 12]}
                      tick={{ fontSize: 10, fill: '#94A3B8' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: '1px solid #E5E7EB', fontSize: 12 }}
                      formatter={(value: any, name: any) => {
                        return [value, tooltipLabels[name] ?? name];
                      }}
                    />
                    <ReferenceLine y={3} stroke="#F59E0B" strokeDasharray="3 2" strokeOpacity={0.25} />
                    <ReferenceLine y={6} stroke="#EA580C" strokeDasharray="3 2" strokeOpacity={0.25} />
                    <ReferenceLine y={9} stroke="#E11D48" strokeDasharray="3 2" strokeOpacity={0.2} />
                    <Bar dataKey="score" radius={[6, 6, 0, 0]} maxBarSize={28}>
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={scoreColor(entry.score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center justify-center gap-4 mt-2">
                  <span className="flex items-center gap-1 text-[10px] text-arete-muted">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#16A34A' }} /> {legendLabels.normal}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-arete-muted">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#F59E0B' }} /> {legendLabels.mild}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-arete-muted">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#EA580C' }} /> {legendLabels.moderate}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-arete-muted">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#E11D48' }} /> {legendLabels.severe}
                  </span>
                </div>
              </div>
            )}
          </>
        );
      })() : (
        <Link to="/checkin" className="card block hover:shadow-card-hover transition-shadow no-underline">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-arete-muted">{noDataPrompt(lens)}</p>
            </div>
            <span className="text-xs font-semibold text-arete-blue whitespace-nowrap ml-4">{noDataCtaLabel(lens)}</span>
          </div>
        </Link>
      )}
    </div>
  );
}

export default DashboardMentalHealthInner;
